import {
  ReadonlyURLSearchParams,
  useParams,
  usePathname,
  useSearchParams,
} from 'next/navigation';
import { Query } from '../types';
import { PATH } from '../../../paths';

export function useNavUtils() {
  const searchParams = useSearchParams();
  const params = useParams();
  const fullPathname = usePathname();
  /**
   * I copied this function from this github issue:
   * https://github.com/vercel/next.js/discussions/40790#discussioncomment-3709854
   *
   * seems to be the only way to have a generatePath function that works
   * like react router, there may be a cleaner solution to this issue
   * but for the mean time, rather than developing it myself, im going
   * to save the time and just use the solution this guy created.
   *
   * It allows for a dynamic path to be passed in, and then the values in the object
   * are automatically put into the route
   *
   * getRouteAsPath(`/foo/[bar]/[id]/form`, { bar: "1", id: 100}, "")
   * -> "/foo/1/100/form"
   * */

  function removeTrailingSlash(path: string) {
    return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
  }

  function generatePath(pathname: string, query?: Query, hash?: string | null) {
    const search: Record<string, string> = {};
    if (searchParams) {
      for (const [key, value] of searchParams.entries()) {
        search[key] = value;
      }
    }

    const routeParams = { ...params, ...query };
    // Replace slugs, and remove them from the `query`
    let asPath = pathname.replace(/\[{1,2}(.+?)]{1,2}/g, ($0, slug: string) => {
      if (slug.startsWith('...')) {
        slug = slug.replace('...', '');
      }

      const value = routeParams[slug]!;
      delete routeParams[slug];
      if (Array.isArray(value)) {
        return value.map((v) => encodeURIComponent(v)).join('/');
      }
      return value !== undefined ? encodeURIComponent(String(value)) : '';
    });

    // Remove any trailing slashes; this can occur if there is no match
    // for a catch-all slug ([[...slug]])
    asPath = removeTrailingSlash(asPath);

    // Ensure query values are strings
    const sP = Object.entries(search).reduce<Record<string, string>>(
      (prev, [key, value]) => {
        prev[key] = [value].join('');
        return prev;
      },
      {},
    );

    // Append remaining query as a querystring, if needed:
    const qs = new URLSearchParams(sP).toString();

    if (qs) asPath += `?${qs}`;
    if (hash) asPath += hash;

    return asPath;
  }

  function matchPath(pathToMatch: (typeof PATH)[keyof typeof PATH]) {
    return (
      fullPathname === generatePath(pathToMatch, { ...params }).split('?')[0]
    );
  }

  return { generatePath, matchPath };
}
